// Frontend mirror of `backend/utils/interest_match.py`.
// Kept in lockstep so the AI Mapping dialog hides/labels the same
// contacts that the backend gate would reject.
//
// Feb 2026 update: mirrors the free-text indication → canonical TA
// expansion introduced backend-side to fix the LQ036 / Sanofi Vaccine BD
// misroute. If you change one file, change the other.

const TA_ALIASES = {
  cns: new Set(['neurology', 'cns/neurology']),
  neurology: new Set(['cns', 'cns/neurology']),
  'cns/neurology': new Set(['cns', 'neurology']),
  respiratory: new Set(['pulmonology', 'respiratory/pulmonology']),
  pulmonology: new Set(['respiratory', 'respiratory/pulmonology']),
  'respiratory/pulmonology': new Set(['respiratory', 'pulmonology']),
  gi: new Set(['gastroenterology']),
  gastroenterology: new Set(['gi']),
  'i&i': new Set(['immunology', 'immunology & inflammation', 'inflammation', 'rheumatology']),
  immunology: new Set(['i&i', 'immunology & inflammation', 'inflammation', 'rheumatology']),
  'immunology & inflammation': new Set(['i&i', 'immunology', 'inflammation', 'rheumatology']),
  inflammation: new Set(['i&i', 'immunology', 'immunology & inflammation', 'rheumatology']),
  rheumatology: new Set(['i&i', 'immunology', 'immunology & inflammation', 'inflammation']),
  cardio: new Set(['cardiovascular']),
  cardiovascular: new Set(['cardio']),
  cardiometabolic: new Set(['metabolic', 'cardiovascular', 'cardio']),
  metabolic: new Set(['cardiometabolic', 'endocrinology']),
  endocrinology: new Set(['metabolic']),
  ophthalmology: new Set(['ophtho', 'retinal']),
  ophtho: new Set(['ophthalmology', 'retinal']),
  hematology: new Set(['heme', 'haematology']),
  haematology: new Set(['hematology', 'heme']),
  heme: new Set(['hematology', 'haematology']),
  'heme-onc': new Set(['hematology-oncology', 'heme oncology']),
  'infectious disease': new Set(['infectious diseases', 'id', 'anti-infective', 'anti-infectives']),
  'infectious diseases': new Set(['infectious disease', 'id', 'anti-infective', 'anti-infectives']),
  renal: new Set(['nephrology', 'kidney']),
  nephrology: new Set(['renal', 'kidney']),
  "women's health": new Set(['womens health', 'gynecology', 'gynaecology']),
  'womens health': new Set(["women's health", 'gynecology', 'gynaecology']),
  'rare disease': new Set(['rare diseases', 'orphan']),
  'rare diseases': new Set(['rare disease', 'orphan']),
};

const INDICATION_KEYWORDS = [
  [['cancer', 'carcinoma', 'tumor', 'tumour', 'neoplasm', 'leukemi',
    'leukaemi', 'lymphoma', 'myeloma', 'sarcoma', 'melanoma', 'glioma',
    'glioblastoma', 'oncolog', 'metasta', 'adenocarcinoma'], 'oncology'],
  [['nsclc', 'sclc', 'hcc', 'crc', 'aml', 'cml', 'mds', 'gbm', 'tnbc',
    'her2+', 'her2 positive', 'prostate cancer', 'breast cancer',
    'lung cancer', 'colorectal', 'pancreatic cancer'], 'oncology'],

  [['copd', 'chronic obstructive'], 'respiratory'],
  [['asthma'], 'respiratory'],
  [['ipf', 'idiopathic pulmonary fibrosis', 'pulmonary fibrosis'], 'respiratory'],
  [['bronchitis', 'cystic fibrosis', 'pulmonary hypertension',
    'pulmonary arterial', 'copd/asthma', 'respirator', 'lung fibrosis'], 'respiratory'],

  [['psorias', 'eczema', 'atopic dermatitis', 'urticaria', 'vitiligo',
    'alopecia areata', 'hidradenitis'], 'immunology'],
  [['psorias', 'eczema', 'atopic dermatitis', 'urticaria', 'vitiligo',
    'alopecia areata', 'hidradenitis'], 'dermatology'],
  [['rheumatoid arthritis', 'psoriatic arthritis', 'ankylosing spondylitis',
    'sle', 'lupus', 'sjogren', 'sjögren', 'scleroderma'], 'immunology'],
  [['crohn', 'ulcerative colitis', 'inflammatory bowel', 'ibd'], 'immunology'],
  [['autoimmun', 'immune-mediated'], 'immunology'],

  [['multiple sclerosis'], 'neurology'],
  [['alzheimer', 'parkinson', 'huntington', 'epilepsy', 'seizure',
    'migraine', 'als', 'amyotrophic lateral', 'neurodegener',
    'neurolog'], 'neurology'],
  [['depression', 'anxiety', 'schizophrenia', 'bipolar', 'psychiat',
    'adhd'], 'neurology'],

  [['amd', 'age-related macular', 'diabetic retinopathy',
    'diabetic macular', 'dme', 'glaucoma', 'dry eye', 'retinal',
    'uveitis', 'keratitis'], 'ophthalmology'],

  [['heart failure', 'hypertension', 'atrial fibrillation', 'coronary',
    'myocardial', 'arrhythmi', 'stroke', 'thrombo', 'cardiovascular',
    'atheroscleros'], 'cardiovascular'],

  [['diabetes', 'type 2', 'type ii diabetes', 'type 1 diabetes',
    'obesity', 'nash', 'mash', 'nafld', 'mafld', 'dyslipidemia',
    'hyperlipid', 'hypercholesterol', 'metabolic syndrome'], 'metabolic'],

  [['hemophili', 'haemophili', 'sickle cell', 'thalassem', 'anemia',
    'anaemia', 'thrombocyt', 'myelofibrosi', 'polycythemi'], 'hematology'],

  [['hepatitis', 'cirrhosis', 'gastro', 'esophageal', 'oesophageal',
    'peptic ulcer', 'reflux', 'gerd', 'pancreatiti'], 'gastroenterology'],

  [['kidney', 'nephro', 'ckd', 'polycystic kidney', 'aki'], 'renal'],

  [['vaccine', 'immunization', 'immunisation', 'prophylactic vacc'], 'vaccines'],

  [['covid', 'sars-cov', 'influenza', 'hiv', 'aids', 'malaria',
    'tuberculosis', 'hepatitis b', 'hepatitis c', 'rsv', 'sepsis',
    'bacterial infection', 'antibiotic', 'antiviral', 'antimicrob',
    'antifungal'], 'infectious disease'],

  [['endometriosis', 'menopause', 'contracept', 'polycystic ovar',
    'pcos', 'uterine fibroid'], "women's health"],

  [['acne', 'rosacea', 'hidradenitis', 'wound healing'], 'dermatology'],

  [['orphan', 'rare disease', 'ultra-rare'], 'rare disease'],
];

const SPLIT_RE = /,|\/|;|\||\+| and | & /i;

// ─── Sub-TA buckets (Feb 2026 v9) — mirror of backend _SUB_TA_BUCKETS
// in utils/interest_match.py. Kept in lockstep.
const SUB_TA_BUCKETS = {
  oncology: {
    hematology_onc: [
      'aml', 'cml', 'all', 'cll', 'mds', 'leukemi', 'leukaemi',
      'lymphoma', 'hodgkin', 'myeloma', 'multiple myeloma',
      'myelofibros', 'polycythemi', 'myelodysplast',
      'myeloprolif', 'hematologic malignanc', 'haematologic malignanc',
      'hematolog', 'haematolog', 'heme onc', 'heme-onc',
      'bone marrow', 'cd123', 'b-cell all', 't-cell lymphoma',
    ],
    genitourinary_onc: [
      'prostate', 'bladder', 'urothelial', 'renal cell', 'kidney cancer',
      'rcc', 'testicular', 'penile', 'urologic', 'genitourinary',
      'gu oncology', 'gu onc',
    ],
    gastric_onc: [
      'gastric', 'stomach cancer', 'gastro-esophageal',
      'gastroesophageal junction', ' gej ', 'esophagogastric',
    ],
    gastrointestinal_onc: [
      'colorectal', ' crc ', 'pancreatic cancer', 'pancreatic adenocarc',
      'hepatocellular', ' hcc ', 'liver cancer', 'cholangiocarc',
      'biliary tract cancer', 'esophageal cancer', 'oesophageal cancer',
      'gi cancer', 'gastrointestinal cancer', 'anal cancer',
    ],
    breast_onc: [
      'breast cancer', 'tnbc', 'her2+', 'her2 positive', 'her2-positive',
      'her2 negative', 'her2-negative', ' hr+ ', 'hormone receptor',
      'ductal carcinoma', 'lobular carcinoma', 'metastatic breast',
    ],
    lung_onc: [
      'nsclc', 'sclc', 'lung cancer', 'lung adenocarc', 'mesothelioma',
      'non-small cell lung', 'small cell lung',
    ],
    gyn_onc: [
      'ovarian cancer', 'cervical cancer', 'endometrial cancer',
      'uterine cancer', 'vulvar', 'gynecologic oncology',
      'gynaecologic oncology', ' gyn onc',
    ],
    cns_onc: [
      'glioma', 'glioblastoma', ' gbm ', 'medulloblastoma',
      'brain tumor', 'brain tumour', 'brain cancer', 'meningioma',
      'astrocytoma', 'ependymoma',
    ],
    skin_onc: [
      'melanoma', 'merkel cell', 'basal cell carcinoma',
      'squamous cell carcinoma of skin', ' cscc', 'cutaneous t-cell',
    ],
    sarcoma_onc: [
      'sarcoma', ' gist ', 'gastrointestinal stromal tumor',
      'ewing', 'osteosarcoma', 'rhabdomyosarcoma', 'leiomyosarcoma',
    ],
    head_and_neck_onc: [
      'head and neck', ' hnscc', 'nasopharyng', 'laryngeal cancer',
      'oropharyng', 'oral cavity cancer', 'salivary gland cancer',
    ],
    endocrine_onc: [
      'thyroid cancer', 'adrenocortical', 'carcinoid',
      'neuroendocrine tumor', ' net ', 'medullary thyroid',
    ],
    ophthalmic_onc: [
      'uveal melanoma', 'retinoblastoma', 'ocular oncology',
    ],
  },
  neurology: {
    alzheimer_dementia: ['alzheimer', 'dementia', 'cognitive impairment', 'mild cognitive impairment', ' mci '],
    parkinsons: ['parkinson', 'dyskinesia', 'levodopa'],
    huntingtons: ['huntington'],
    multiple_sclerosis: ['multiple sclerosis', ' ms ', 'nmosd', 'neuromyelitis optica', 'demyelinating'],
    als_motor_neuron: [' als ', 'amyotrophic lateral', 'motor neuron disease'],
    epilepsy_seizure: ['epilepsy', 'seizure', 'dravet', 'lennox-gastaut', 'status epilepticus'],
    migraine_pain: ['migraine', 'cluster headache', 'chronic pain', 'neuropathic pain', 'fibromyalgi'],
    psychiatry: ['depression', 'major depressive', ' mdd ', 'anxiety disorder', ' ptsd ', ' ocd ', 'schizophrenia', 'bipolar', 'psychiat', ' adhd ', 'substance use', 'addiction'],
    neuromuscular_rare: ['myasthenia gravis', 'muscular dystrophy', 'spinal muscular atrophy', ' sma ', 'duchenne', 'becker', 'friedreich', 'rett syndrome'],
    stroke_neuro: ['stroke', 'ischemic stroke', 'subarachnoid hemorrhage', 'traumatic brain', ' tbi ', 'cerebral hemorrhage'],
    sleep_neuro: ['insomnia', 'narcolepsy', 'sleep apnea'],
  },
  immunology: {
    dermatology_immuno: ['psorias', 'atopic dermatitis', 'eczema', 'vitiligo', 'urticaria', 'alopecia areata', 'hidradenitis suppurativa'],
    gi_immuno: ['crohn', 'ulcerative colitis', 'inflammatory bowel', ' ibd ', 'celiac disease'],
    rheumatology: ['rheumatoid arthritis', ' ra ', 'psoriatic arthritis', 'ankylosing spondylitis', 'spondyloarthritis', 'gout', 'juvenile idiopathic arthritis'],
    connective_tissue: [' sle ', 'lupus', 'sjogren', 'sjögren', 'scleroderma', 'myositis', 'dermatomyositis', 'polymyositis'],
    vasculitis: ['vasculiti', 'giant cell arteritis', ' anca', 'granulomatosis', 'kawasaki'],
    transplant_immuno: ['transplant rejection', ' gvhd ', 'graft versus host', 'graft-versus-host'],
    allergy_immuno: ['food allergy', 'allergic asthma', 'eosinophilic esophag', 'anaphylaxis', 'hereditary angioedema'],
  },
  cardiovascular: {
    heart_failure: ['heart failure', ' hfpef', ' hfref', 'cardiomyopathy'],
    afib_arrhythmia: ['atrial fibrillation', 'arrhythmi', 'ventricular tachyc', 'torsade'],
    stroke_thrombosis: ['stroke', 'thromboembolic', ' dvt ', 'pulmonary embolism', 'venous thromboembolism', ' vte '],
    hypertension: ['hypertension', 'resistant hypert'],
    atherosclerosis: ['atheroscleros', 'coronary artery disease', ' cad ', 'myocardial', ' acs ', 'acute coronary'],
    lipid_disorder: ['dyslipidemia', 'hyperlipid', 'hypercholesterol', 'familial hypercholester', ' fh ', ' hofh', ' hefh'],
    pulmonary_hypertension: ['pulmonary arterial hypertension', ' pah ', 'pulmonary hypertension'],
  },
  respiratory: {
    copd: ['copd', 'chronic obstructive', 'chronic bronchitis', 'emphysema'],
    asthma: ['asthma', 'eosinophilic asthma', 'severe asthma'],
    ipf_ild: [' ipf ', 'idiopathic pulmonary fibrosis', 'pulmonary fibrosis', 'interstitial lung', 'lung fibrosis', ' ild '],
    cystic_fibrosis: ['cystic fibrosis', ' cf ', 'bronchiectasis'],
    allergy_rhinitis: ['allergic rhinitis', 'chronic rhinosinusitis', 'nasal polyp'],
  },
  metabolic: {
    diabetes_t2: ['type 2 diabetes', ' t2d', 'type ii diabetes'],
    diabetes_t1: ['type 1 diabetes', ' t1d', 'type i diabetes'],
    obesity: ['obesity', 'weight loss', 'weight management', ' glp-1', ' glp1 ', 'overweight'],
    nash_mash: [' nash ', ' mash ', ' nafld', ' mafld', 'non-alcoholic steatohep', 'metabolic dysfunction-associated'],
    dyslipidemia_metab: ['dyslipidemia', 'hyperlipid', 'hypercholesterol', 'familial hypercholester'],
    rare_metabolic: ['pompe', 'gaucher', 'fabry', 'mucopolysaccharid', ' mps ', 'phenylketonuria', ' pku '],
    thyroid_metab: ['hypothyroid', 'hyperthyroid', "graves' disease"],
  },
  'infectious disease': {
    hiv: [' hiv ', ' aids '],
    hepatitis_viral: ['hepatitis b', ' hbv', 'hepatitis c', ' hcv', 'hepatitis d', ' hdv'],
    covid_flu_rsv: ['covid', 'sars-cov', 'influenza', ' flu ', 'respiratory syncytial', ' rsv '],
    tuberculosis: ['tuberculosis', ' tb ', 'mycobacterium'],
    antibacterial: ['bacterial infection', 'antibiotic', 'antibacterial', ' mrsa', 'gram-negative', 'sepsis'],
    antifungal: ['antifungal', ' candida ', 'aspergillus', 'cryptococcus'],
    antiviral_other: [' cmv ', ' hsv ', ' ebv ', 'herpes'],
    parasitic: ['malaria', 'leishmania', ' chagas', 'schistosomiasis'],
  },
  ophthalmology: {
    amd: [' amd ', 'age-related macular', 'wet amd', 'dry amd', 'geographic atrophy'],
    diabetic_eye: ['diabetic retinopathy', 'diabetic macular', ' dme '],
    glaucoma: ['glaucoma', ' iop ', 'intraocular pressure'],
    dry_eye: ['dry eye', 'keratoconjunctivitis sicca'],
    uveitis: ['uveitis'],
    retinal_dystrophy: ['retinitis pigmentosa', 'stargardt', 'inherited retinal', 'leber congenital amaurosis'],
    corneal: ['corneal', 'keratitis', 'keratoconus'],
  },
  hematology: {
    hemophilia: ['hemophili', 'haemophili', 'factor viii', 'factor ix'],
    sickle_thalassemia: ['sickle cell', 'thalassem'],
    thrombocytopenia: ['thrombocyt', ' itp ', 'immune thrombocytop'],
    anemia_nutritional: ['iron deficiency', 'aplastic anemia', 'aplastic anaemia'],
    coagulation_rare: ['von willebrand', 'atypical hemolytic'],
  },
  renal: {
    ckd: ['chronic kidney disease', ' ckd ', ' esrd ', 'end-stage renal'],
    aki: ['acute kidney injury', ' aki '],
    pkd: ['polycystic kidney', 'autosomal dominant polycystic', ' adpkd'],
    igan: ['iga nephropathy', ' igan '],
    lupus_nephritis: ['lupus nephritis'],
    rare_renal: ['alport', 'focal segmental glomerulo', ' fsgs ', 'membranous nephropathy'],
  },
  "women's health": {
    endometriosis: ['endometriosis', 'adenomyosis'],
    menopause: ['menopause', 'hot flash', 'vasomotor'],
    contraception: ['contracept'],
    pcos: [' pcos ', 'polycystic ovar'],
    uterine_fibroid: ['uterine fibroid', ' myoma'],
    pregnancy_disorders: ['preeclampsia', 'gestational diabetes', 'postpartum'],
  },
  dermatology: {
    acne_rosacea: ['acne', 'rosacea'],
    wound_healing: ['wound healing', 'chronic wound', 'diabetic foot ulcer'],
    pigmentation: ['hyperpigmentation', 'melasma'],
  },
};

const _subTaBucketsFor = (text, parentTa) => {
  const buckets = SUB_TA_BUCKETS[parentTa];
  if (!buckets) return new Set();
  const t = _normalize(text);
  if (!t) return new Set();
  const padded = ` ${t} `;
  const hits = new Set();
  for (const [bucket, keywords] of Object.entries(buckets)) {
    for (const kw of keywords) {
      if (padded.includes(kw) || t.includes(kw)) {
        hits.add(bucket);
        break;
      }
    }
  }
  return hits;
};

const _allSubTaBucketsFor = (areas, parentTa) => {
  const out = new Set();
  for (const a of (areas || [])) {
    for (const x of _subTaBucketsFor(a, parentTa)) out.add(x);
  }
  return out;
};

// Reverse alias index: alias → set of granular parent TAs. Built once
// at module load so `_canonicalParentsOf` runs in O(1) per call.
const ALIAS_TO_CANONICAL = {};
for (const [_parent, _aliases] of Object.entries(TA_ALIASES)) {
  if (SUB_TA_BUCKETS[_parent]) {
    for (const _alias of _aliases) {
      if (!ALIAS_TO_CANONICAL[_alias]) ALIAS_TO_CANONICAL[_alias] = new Set();
      ALIAS_TO_CANONICAL[_alias].add(_parent);
    }
  }
}

const _canonicalParentsOf = (ta) => {
  const t = _normalize(ta);
  if (SUB_TA_BUCKETS[t]) return new Set([t]);
  const hits = ALIAS_TO_CANONICAL[t];
  if (hits) return new Set(hits);
  return new Set([t]);
};

const _normalize = (v) => String(v || '').trim().toLowerCase();

const _splitArea = (area) => {
  const a = _normalize(area);
  if (!a) return [];
  const pieces = a
    .split(SPLIT_RE)
    .map((p) => p.trim())
    .filter(Boolean);
  if (!pieces.includes(a)) pieces.push(a);
  return pieces;
};

const _canonicalTasFromToken = (token) => {
  const t = _normalize(token);
  if (!t) return new Set();
  const out = new Set([t]);
  for (const alias of (TA_ALIASES[t] || [])) out.add(alias);
  for (const [keywords, ta] of INDICATION_KEYWORDS) {
    if (keywords.some((k) => t.includes(k))) {
      out.add(ta);
      for (const alias of (TA_ALIASES[ta] || [])) out.add(alias);
    }
  }
  return out;
};

const _expand = (area) => {
  if (!area) return new Set();
  const tokens = _splitArea(area);
  const out = new Set();
  for (const tok of tokens) {
    for (const x of _canonicalTasFromToken(tok)) out.add(x);
  }
  return out;
};

/**
 * @param {string[] | null | undefined} statedInterests
 * @param {string | null | undefined} assetTherapeuticArea
 * @returns {{allowed: boolean, reason: string}}
 */
export function isOutreachAllowed(statedInterests, assetTherapeuticArea) {
  const stated = (statedInterests || []).filter((s) => s && String(s).trim());
  if (stated.length === 0) return { allowed: true, reason: '' };
  const ta = String(assetTherapeuticArea || '').trim();
  if (!ta) return { allowed: true, reason: '' };

  const assetExp = _expand(ta);
  if (assetExp.size === 0) return { allowed: true, reason: '' };

  const statedExp = new Set();
  for (const s of stated) {
    for (const x of _expand(s)) statedExp.add(x);
  }
  const shared = new Set();
  for (const x of statedExp) {
    if (assetExp.has(x)) shared.add(x);
  }
  if (shared.size === 0) return { allowed: false, reason: 'interest_mismatch' };

  // Feb 2026 (v9) — generalized sub-TA granularity guard.
  // For every shared parent TA, if both sides declare specific sub-buckets
  // under it, they must intersect. Non-granular parents rescue on broad.
  // Aliases (cns → neurology, pulmonology → respiratory, i&i → immunology)
  // are canonicalized so they don't accidentally bypass the sub-check.
  const canonicalParents = new Set();
  for (const p of shared) {
    for (const q of _canonicalParentsOf(p)) canonicalParents.add(q);
  }
  let rescued = false;
  for (const parent of canonicalParents) {
    if (!SUB_TA_BUCKETS[parent]) {
      rescued = true;
      break;
    }
    const assetSub = _subTaBucketsFor(assetTherapeuticArea, parent);
    const statedSub = _allSubTaBucketsFor(stated, parent);
    if (statedSub.size === 0 || assetSub.size === 0) {
      rescued = true;
      break;
    }
    let anySubShared = false;
    for (const x of statedSub) if (assetSub.has(x)) { anySubShared = true; break; }
    if (anySubShared) {
      rescued = true;
      break;
    }
  }
  if (!rescued) return { allowed: false, reason: 'sub_ta_mismatch' };
  return { allowed: true, reason: '' };
}

/**
 * Find contacts at a company whose stated `interest_areas` cover the
 * asset's therapeutic area / indication. Feb 2026: accepts free-text
 * indication strings via the shared _expand pipeline.
 *
 * @param {Array<{interest_areas?: string[]}>} contacts
 * @param {string | null | undefined} assetTherapeuticArea
 * @returns {Array}
 */
export function findSpecialists(contacts, assetTherapeuticArea) {
  if (!assetTherapeuticArea || !String(assetTherapeuticArea).trim()) return [];
  const out = [];
  for (const c of (contacts || [])) {
    // Sep 2026 · a bounced / departed contact can never hold the specialist lock.
    if (c?.email_status === 'bounced' || c?.email_status === 'left_company') continue;
    const stated = Array.isArray(c?.interest_areas) ? c.interest_areas : [];
    if (stated.length === 0) continue;
    const { allowed } = isOutreachAllowed(stated, assetTherapeuticArea);
    if (allowed) out.push(c);
  }
  return out;
}

// True when a rule's TA list overlaps the asset's expanded TA + indication
// signal (same taxonomy the send gate uses). Used for BD Recipient Rules.
export function areasOverlap(ruleAreas, assetSignal) {
  const asset = _expand(assetSignal || '');
  if (!asset.size) return false;
  for (const ra of ruleAreas || []) {
    const r = _expand(ra || '');
    for (const t of r) if (asset.has(t)) return true;
  }
  return false;
}

/**
 * Sep 2026 (NuGel / AbbVie): the specialist LOCK must be earned by a DIRECT
 * therapeutic-area match — a contact tagged with the asset's own TA family
 * (Dermatology ↔ dermatology / skin / derm). Indication-derived adjacency
 * (Immunology people qualifying for an atopic-dermatitis asset) still makes
 * those contacts *allowed* via isOutreachAllowed, but must not hide every
 * other colleague behind them. Pass the TA ONLY (no indication).
 */
// "Dermatological disease" / "Dermatologic disorders" / "Skin diseases" → "dermatology";
// "Neurological disease" → "neurology"; "Oncology" stays. Family-level stem only.
const _FAMILY_STEMS = [
  [/^(dermatolog|skin)/, 'dermatology'], [/^(neurolog|cns|neuroscience)/, 'neurology'], [/^(oncolog|cancer|tumou?r)/, 'oncology'],
  [/^(immunolog|inflammat|i&i|rheumatolog)/, 'immunology'], [/^(ophthalm|eye|retina)/, 'ophthalmology'], [/^(cardio|cardiovascular|heart)/, 'cardiovascular'],
  [/^(respirat|pulmon|lung)/, 'respiratory'], [/^(gastro|gi\b|digestive)/, 'gastroenterology'], [/^(endocrin|metabol|diabet)/, 'endocrinology'],
  [/^(infectio|virolog|antiviral|antibacterial)/, 'infectious disease'], [/^(hematolog|haematolog|blood)/, 'hematology'], [/^(nephrolog|renal|kidney)/, 'nephrology'],
  [/^(psychiatr|mental)/, 'psychiatry'], [/^(rare|orphan)/, 'rare disease'], [/^(women|gynecolog|obstetric)/, "women's health"], [/^(urolog)/, 'urology'],
];
const _familyStem = (tok) => {
  const t = _normalize(tok);
  for (const [re, fam] of _FAMILY_STEMS) if (re.test(t)) return fam;
  return t;
};

export function findDirectSpecialists(contacts, assetTherapeuticAreaOnly) {
  const ta = String(assetTherapeuticAreaOnly || '').trim();
  if (!ta) return [];
  const assetFamilies = new Set();
  for (const tok of _expand(ta)) for (const p of _canonicalParentsOf(_familyStem(tok))) assetFamilies.add(p);
  if (!assetFamilies.size) return [];
  const out = [];
  for (const c of (contacts || [])) {
    const stated = Array.isArray(c?.interest_areas) ? c.interest_areas : [];
    if (!stated.length) continue;
    let direct = false;
    for (const s of stated) {
      for (const tok of _splitArea(s)) {
        for (const p of _canonicalParentsOf(_familyStem(tok))) if (assetFamilies.has(p)) direct = true;
        // Indication tags (e.g. "Atopic dermatitis") count as direct when the
        // indication belongs to the asset's own TA family.
        for (const alias of _canonicalTasFromToken(tok)) {
          for (const p of _canonicalParentsOf(_familyStem(alias))) if (assetFamilies.has(p)) direct = true;
        }
      }
    }
    if (direct) out.push(c);
  }
  return out;
}

// "CNS, CNS (Central Nervous System), CNS/Psychiatry, Psychiatry, Migraine, Pain,
// Parkinson's Disease, neurodegeneration, Neurology / CNS" → "Neurology, Psychiatry, Pain +6 tags".
export function summarizeInterests(stated, max = 3) {
  const tags = (stated || []).map((s) => String(s || '').trim()).filter(Boolean);
  if (!tags.length) return '';
  const fams = [];
  for (const t of tags) {
    const f = _familyStem(t.split(/[\/(]/)[0].trim());
    const label = f === _normalize(t.split(/[\/(]/)[0].trim()) ? t.split(/[\/(]/)[0].trim() : f.charAt(0).toUpperCase() + f.slice(1);
    if (!fams.some((x) => x.toLowerCase() === label.toLowerCase())) fams.push(label);
  }
  const shown = fams.slice(0, max).join(', ');
  const extra = tags.length - Math.min(fams.length, max);
  return fams.length > max || tags.length > fams.length ? `${shown} +${Math.max(extra, fams.length - max)} tags` : shown;
}

// ── Contact-level exclusion coverage (Sep 2026, Takeda/HB0056) ──────────
// Mirrors backend services/hard_signals._excl_covers_asset: an exclusion
// covers an asset only when it names the asset's TA itself, or matches EVERY
// listed indication — never on a bare substring ("RA" ⊂ "respiRAtory",
// "IBS" vs "Inflammatory Bowel Disease" were false positives).
const _exNorm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9 &]+/g, ' ').replace(/\s+/g, ' ').trim();
const _wordIn = (needle, hay) => {
  if (!needle || !hay) return false;
  if (needle === hay) return true;
  return new RegExp(`(^| )${needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}( |$)`).test(hay);
};
function _indicationMatches(excl, ind) {
  const ne = _exNorm(excl), ni = _exNorm(ind);
  if (!ne || !ni) return false;
  if (ne === ni) return true;
  // whole-phrase containment only (multi-word or ≥5 chars) — abbreviations must be exact
  const longEnough = ne.includes(' ') || ne.length >= 5;
  return longEnough && (_wordIn(ne, ni) || _wordIn(ni, ne));
}
export function exclusionCoversAsset(excl, therapeuticArea, indication) {
  const ne = _exNorm(excl);
  if (!ne) return false;
  const tas = String(therapeuticArea || '').split(/[·;,/]/).map(_exNorm).filter(Boolean);
  if (tas.some((t) => t === ne || (ne.length >= 5 && _wordIn(ne, t)))) return true;
  const inds = String(indication || '').split(/[·;,/]/).map((x) => x.trim()).filter(Boolean);
  if (!inds.length) return false;
  return inds.every((i) => _indicationMatches(excl, i));
}
